import React from 'react';
import { RailsPredictiveSearchResult } from '@sls-react/typings/services/rails/predictiveSearch';
import { useQuery } from 'react-query';
import { AnalyticsSource } from '@sls-react/services/analytics/AnalyticsInterfaces';
import { fetchPredictiveSearch } from '@sls-react/api/landers';
import { useRouter } from '@sls-react/routing';
import qsStringify from '@sls-react/utils/qsStringify';
import useDebounceValue from '@sls-react/hooks/useDebounceValue';

const availableSortFn = (a, b) => b.available_count - a.available_count;

function getPathPrefix(match: RailsPredictiveSearchResult) {
  if (match.type === 'navigation') {
    return '/nav/';
  } else {
    return '/shop/';
  }
}

const src: AnalyticsSource = 'predictive-search';

export default function usePredictiveSearch(
  query = '',
  max = 10,
): [
  RailsPredictiveSearchResult[],
  (e: React.FormEvent, query: string) => void,
] {
  const debouncedQuery = useDebounceValue(query, 150);

  const modifiedQuery = debouncedQuery.trim();

  const { data: predictiveResults } = useQuery(
    ['predictive-search', modifiedQuery],
    () => fetchPredictiveSearch(modifiedQuery),
    {
      enabled: !!modifiedQuery && modifiedQuery.length > 2,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: 20000,
    },
  );

  return [
    predictiveResults?.slice?.(0, max) || [],
    (e, rawTypedQuery) => {
      e.preventDefault();

      const typedQuery = rawTypedQuery.trim();

      if (!predictiveResults) {
        // If there's multiple matches, go to search
        window.location.assign(`/search${qsStringify({ q: typedQuery, src })}`);
        return;
      }

      try {
        const re = new RegExp(`^${typedQuery}$`, 'i');

        const directMatches = predictiveResults
          .filter(r => re.test(r.name))
          .sort(availableSortFn);

        const synonymMatches = predictiveResults
          .filter(r => r.synonyms && r.synonyms.some(s => re.test(s)))
          .sort(availableSortFn);

        if (directMatches.length > 0) {
          window.location.assign(
            `${getPathPrefix(directMatches[0])}${
              directMatches[0].path
            }${qsStringify({ src })}`,
          );
        } else if (synonymMatches.length > 0) {
          window.location.assign(
            `${getPathPrefix(synonymMatches[0])}${
              synonymMatches[0].path
            }${qsStringify({ src })}`,
          );
        } else {
          window.location.assign(
            `/search${qsStringify({ q: typedQuery, src })}`,
          );
        }
      } catch (e) {
        window.location.assign(`/search${qsStringify({ q: typedQuery, src })}`);
      }
    },
  ];
}
